import { canCreate } from "@/lib/perms";
import { pageRaw } from "@/lib/blocks";
import { Portrait } from "@/components/ShipBits";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useWorld } from "@/context/WorldContext";
import { OriginBadge, useOrigin } from "@/components/Origin";
import { extractLinks, findByTitle } from "@/lib/api";
import { catMeta } from "@/lib/constants";

export default function Timeline() {
  const { basePath, readOnly, world } = useWorld();
  const { pages, toggle } = useOrigin();
  const [q, setQ] = useState("");
  const events = pages
    .filter((p) => p.category === "Events")
    .filter((p) => !q || `${p.title} ${p.summary} ${pageRaw(p)}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (a.event_order ?? Infinity) - (b.event_order ?? Infinity) || a.title.localeCompare(b.title));

  return (
    <div className="px-6 md:px-12 py-10 max-w-5xl" data-testid="timeline-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <p className="font-code text-xs uppercase tracking-[0.3em] text-rose-400/80 mb-2">Chronicle</p>
          <h1 className="font-display text-4xl sm:text-5xl text-amber-50">The long count of ages</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {toggle}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name, place..." className="w-56" data-testid="timeline-filter-input" />
          {!readOnly && canCreate(world, "Events") && (
            <Link to={`${basePath}/new?category=Events`} className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-sm font-semibold px-4" data-testid="add-event-btn">
              <Plus className="w-4 h-4" /> Event
            </Link>
          )}
        </div>
      </div>
      {events.length === 0 ? (
        <p className="text-slate-500 italic" data-testid="timeline-empty">No events recorded. Create a page in the Events category to begin the chronicle.</p>
      ) : (
        <ol className="relative border-l border-line ml-3 space-y-10">
          {events.map((e, i) => {
            const linked = [...new Set(extractLinks(pageRaw(e)))].map((t) => findByTitle(pages, t)).filter(Boolean);
            const cast = pages.filter((p) => p.category === "Characters" && (p.blocks || []).some((b) => b.kind === "timeline" && b.points.some((pt) => pt.event_id === e.id)));
            return (
              <li key={e.id} className="pl-8 relative rise" style={{ animationDelay: `${i * 70}ms` }} data-testid={`timeline-event-${e.id}`}>
                <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full bg-canvas border-2 border-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.5)]" />
                <p className="font-code text-xs text-rose-300 tracking-wide">{e.event_date || "Undated"}</p>
                <Link to={`${basePath}/p/${e.id}`} className="font-display text-2xl md:text-3xl text-amber-50 hover:text-amber-300 transition-colors">{e.title}</Link> <OriginBadge page={e} className="ml-1" />
                {e.summary && <p className="text-slate-400 mt-1 max-w-2xl">{e.summary}</p>}
                {cast.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`timeline-cast-${e.id}`}>
                    {cast.map((c) => (
                      <Link key={c.id} to={`${basePath}/p/${c.id}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-100 pl-0.5 pr-2.5 py-0.5 hover:bg-amber-500/20" data-testid={`timeline-cast-chip-${c.id}`}>
                        <Portrait page={c} size="w-5 h-5 text-[8px]" />{c.title}
                      </Link>
                    ))}
                  </div>
                )}
                {linked.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {linked.map((p) => {
                      const m = catMeta(p.category);
                      return <Link key={p.id} to={`${basePath}/p/${p.id}`} className={`text-xs rounded-full border px-2.5 py-0.5 ${m.border} ${m.text} ${m.bg} hover:brightness-125`}>{p.title}</Link>;
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
